import { useAuthStore } from '@/features/auth/useAuthStore.js'
import { createNoAccessPath } from '@/features/noAccess/noAccess.utils.js'
import { TenantType } from '@/features/organization/organizationTypeName.js'
import { decidePermission } from '@/features/permissions/permissionEngine.js'
import { getActiveClubId, useActiveClub } from '@/features/router/useActiveClub.js'

import { createAdminSidebarMenuLinkItems } from './useAdminSidebarMenuLinks.js'

function getRouteActiveClubId(to) {
  const activeClubId = to?.query?.activeClubId
  return Array.isArray(activeClubId) ? activeClubId[0] : activeClubId
}

function getAdminRootRedirectCandidates() {
  const { activeClub } = useActiveClub()
  const canShowTenantManagementLinks = activeClub?.tenantType === TenantType.Club

  return createAdminSidebarMenuLinkItems({ canShowTenantManagementLinks }).flatMap(item => {
    if (!item.to) return []

    const actions =
      item.permissionAnyActions || (item.permissionAction ? [item.permissionAction] : [])
    return actions.map(action => [action, item.to])
  })
}

export function preserveRouteQuery(path, to) {
  const query = to?.query ?? {}
  const queryEntries = Object.entries(query).filter(([, value]) => value !== undefined)

  if (!queryEntries.length) return path

  return {
    path,
    query: Object.fromEntries(queryEntries),
  }
}

export function resolveFirstVisibleAdminMenuRoute(to, { fallbackToFirstItem = false } = {}) {
  const authStore = useAuthStore()
  const { activeClub } = useActiveClub()
  const activeTenantId = getRouteActiveClubId(to) || getActiveClubId()
  const candidates = getAdminRootRedirectCandidates()

  for (const [action, path] of candidates) {
    const decision = decidePermission(action, {
      myDetails: authStore.myDetails,
      activeTenantId,
      activeTenant: activeClub,
      surface: 'admin',
      route: path,
    })

    if (decision.allowed) return preserveRouteQuery(path, to)
  }

  if (fallbackToFirstItem && candidates[0]?.[1]) {
    return preserveRouteQuery(candidates[0][1], to)
  }

  return null
}

export function resolveAdminRootRedirect(to) {
  return (
    resolveFirstVisibleAdminMenuRoute(to) ||
    createNoAccessPath({
      reason: 'permission-denied',
      action: 'admin',
      returnUrl: to.fullPath,
    })
  )
}
